import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import { searchFlat } from '../../../qwanyx-core/api-client';
import { DNDEditor } from '../../editor/DNDEditor';
import { QwantaProvider } from 'qwanyx';
import { getTemplateCache } from '../services/templateCache';
import { 
  getSelectionStyle, 
  getBackgroundColor
} from '../utils/stackUtils';

/**
 * SearchableGrid - Grid view with search capabilities
 * 
 * This component provides a grid layout of search results with:
 * - Grid display with configurable columns
 * - Search functionality like FlatView
 * - Infinite scroll pagination
 * - Template rendering for grid items
 * - Virtualization for performance
 * 
 * Props:
 * - rootId: Root ID to scope search within (optional)
 * - di: Domain ID for filtering (optional)
 * - query: Search query string
 * - fields: Fields to search in (default: all text fields)
 * - fuzzy: Enable fuzzy search
 * - facets: Facet fields to generate
 * - pageSize: Items per page (default: 50)
 * - onSelect: Selection callback
 * - selectedItems: Currently selected items
 * - itemTemplate: Template for rendering grid items
 * - useCompact: Use compact rendering
 * - debug: Enable debug logging
 * - columnsPerRow: Number of columns in grid
 * - itemWidth: Width of each grid item
 * - itemHeight: Height of each grid item
 * - gap: Gap between grid items
 */

const SearchableGrid = ({
  rootId = null,
  di = null,
  query = '',
  fields = [],
  fuzzy = false,
  facets = [],
  facetConfig = [], // Array of {field, label} objects for custom facet configuration
  pageSize = 50,
  onSelect,
  selectedItems = [],
  itemTemplate = null,
  useCompact = true,
  debug = false,
  // UI props
  showSearchInfo = true,
  showLoadingState = true,
  showFacets = true,
  showFacetCounts = true,
  // Grid-specific props
  columnsPerRow = 3,
  itemWidth = 250,
  itemHeight = 300,
  gap = 16,
  // Sorting props
  sortBy = 'title',
  sortOrder = 1
}) => {
  // State management (same as FlatView)
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [facetData, setFacetData] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  
  // Template state
  const [templateData, setTemplateData] = useState(null);
  const [processedTemplates, setProcessedTemplates] = useState(new Map());
  
  // Refs for height measurement
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(600);
  const [containerWidth, setContainerWidth] = useState(800);

  // Measure container dimensions
  useEffect(() => {
    const measureDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newHeight = rect.height;
        const newWidth = rect.width;
        if ((newHeight !== containerHeight || newWidth !== containerWidth) && newHeight > 0 && newWidth > 0) {
          setContainerHeight(newHeight);
          setContainerWidth(newWidth);
          if (debug) {
            console.log('[SearchableGrid] Container dimensions updated:', { width: newWidth, height: newHeight });
          }
        }
      }
    };
    
    // Use requestAnimationFrame to ensure DOM has updated
    const measureWithDelay = () => {
      requestAnimationFrame(() => {
        setTimeout(measureDimensions, 100);
      });
    };
    
    measureWithDelay();
    
    // Set up ResizeObserver for more accurate dimension tracking
    let resizeObserver;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newHeight = entry.contentRect.height;
          const newWidth = entry.contentRect.width;
          if ((newHeight !== containerHeight || newWidth !== containerWidth) && newHeight > 0 && newWidth > 0) {
            setContainerHeight(newHeight);
            setContainerWidth(newWidth);
            if (debug) {
              console.log('[SearchableGrid] ResizeObserver dimensions update:', { width: newWidth, height: newHeight });
            }
          }
        }
      });
      resizeObserver.observe(containerRef.current);
    }
    
    // Fallback to window resize listener
    window.addEventListener('resize', measureWithDelay);
    
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', measureWithDelay);
    };
  }, [showSearchInfo, showFacets, showLoadingState, debug]); // Remove containerHeight/Width to prevent infinite loop

  // Load template (same as FlatView)
  useEffect(() => {
    if (itemTemplate) {
      // Force refresh on mount to ensure fresh templates during development
      getTemplateCache().fetchTemplate(itemTemplate, true)
        .then(template => {
          setTemplateData(template);
          if (debug) {
            console.log('[SearchableGrid] Template loaded with refresh:', itemTemplate);
          }
        })
        .catch(error => {
          console.error('[SearchableGrid] Error loading template:', error);
        });
    } else {
      setTemplateData(null);
      setProcessedTemplates(new Map());
    }
  }, [itemTemplate, debug]);

  // Process templates (same as FlatView)
  useEffect(() => {
    if (templateData && items.length > 0) {
      const newProcessedTemplates = new Map();
      
      items.forEach(item => {
        try {
          const processedTemplate = getTemplateCache().replaceTemplateVariables(templateData, item);
          newProcessedTemplates.set(item._id, processedTemplate);
        } catch (error) {
          console.error(`[SearchableGrid] Error processing template for item ${item._id}:`, error);
        }
      });
      
      setProcessedTemplates(newProcessedTemplates);
    }
  }, [templateData, items, debug]);

  // Search function (same as FlatView)
  const performSearch = useCallback(async (reset = false) => {
    if (loading) return;
    
    const skip = reset ? 0 : currentPage * pageSize;
    
    if (debug) {
      console.log('[SearchableGrid] Performing search:', {
        rootId, di, query, fields, fuzzy, facets,
        skip, limit: pageSize, reset
      });
    }
    
    setLoading(true);
    if (reset) {
      setError(null);
    }
    
    try {
      const result = await searchFlat(
        rootId, di, query, fields, fuzzy, facets,
        skip, pageSize, sortBy, sortOrder
      );
      
      if (debug) {
        console.log('[SearchableGrid] Search result:', result);
      }
      
      if (reset) {
        setItems(result.results);
        setCurrentPage(1);
      } else {
        setItems(prev => [...prev, ...result.results]);
        setCurrentPage(prev => prev + 1);
      }
      
      setFacetData(result.facets || {});
      setTotalCount(result.total);
      setHasMore(result.results.length === pageSize && (skip + result.results.length) < result.total);
      
    } catch (err) {
      console.error('[SearchableGrid] Search error:', err);
      setError(err.message);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [rootId, di, query, fields, fuzzy, facets, pageSize, currentPage, loading, debug, sortBy, sortOrder]);

  // Initial search and reset on query change
  useEffect(() => {
    setItems([]);
    setCurrentPage(0);
    setHasMore(true);
    performSearch(true);
  }, [rootId, di, query, fields, fuzzy, facets, pageSize, sortBy, sortOrder]);

  // Handle item selection
  const handleItemSelect = (item, isShiftKey = false, isCtrlKey = false) => {
    if (onSelect) {
      onSelect(item._id, isShiftKey, isCtrlKey);
    }
  };

  // Calculate grid dimensions
  const columnCount = columnsPerRow;
  const rowCount = Math.ceil(items.length / columnCount);
  const actualItemWidth = (containerWidth - (columnCount + 1) * gap) / columnCount;

  // Grid cell renderer
  const GridCell = ({ columnIndex, rowIndex, style }) => {
    const itemIndex = rowIndex * columnCount + columnIndex;
    
    if (itemIndex >= items.length) {
      return null;
    }
    
    const item = items[itemIndex];
    const isSelected = selectedItems.includes(item._id);
    const processedTemplate = processedTemplates.get(item._id);
    
    // Check if near end for infinite scroll
    const isNearEnd = itemIndex >= items.length - (columnsPerRow * 2);
    if (isNearEnd && hasMore && !loading) {
      setTimeout(() => performSearch(false), 0);
    }
    
    // Template rendering
    if (processedTemplate) {
      return (
        <div style={{
          ...style,
          left: style.left + gap,
          top: style.top + gap,
          width: actualItemWidth,
          height: itemHeight - gap
        }}>
          <div
            onClick={(e) => handleItemSelect(item, e.shiftKey, e.ctrlKey)}
            style={{
              ...getSelectionStyle(isSelected, false, true),
              backgroundColor: getBackgroundColor(isSelected, false),
              padding: '8px',
              borderRadius: '8px',
              cursor: 'pointer',
              border: '1px solid #eee',
              height: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
          >
            <QwantaProvider _id={item._id}>
              <DNDEditor
                cardData={processedTemplate}
                enabled={false}
                readOnly={true}
                stackMode={false}
                compact={useCompact}
              />
            </QwantaProvider>
          </div>
        </div>
      );
    }
    
    // Default rendering
    const displayText = item.title || 
      (item.firstname && item.name ? `${item.firstname} ${item.name}` : 'Untitled');
    
    return (
      <div style={{
        ...style,
        left: style.left + gap,
        top: style.top + gap,
        width: actualItemWidth,
        height: itemHeight - gap
      }}>
        <div
          onClick={(e) => handleItemSelect(item, e.shiftKey, e.ctrlKey)}
          style={{
            ...getSelectionStyle(isSelected, false, true),
            backgroundColor: getBackgroundColor(isSelected, false),
            padding: '12px',
            borderRadius: '8px',
            cursor: 'pointer',
            border: '1px solid #eee',
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}
        >
          {/* Icon */}
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: item.isFolder ? '#e3f2fd' : '#f3e5f5',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            marginBottom: '12px'
          }}>
            {item.isFolder ? '📁' : '📄'}
          </div>
          
          {/* Title */}
          <div style={{ 
            fontWeight: '500', 
            fontSize: '14px',
            marginBottom: '8px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {displayText}
          </div>
          
          {/* Brief */}
          {item.brief && (
            <div style={{ 
              fontSize: '12px', 
              color: '#666',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}>
              {item.brief}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Helper function to get facet label
  const getFacetLabel = (fieldName) => {
    const config = facetConfig.find(f => f.field === fieldName);
    return config ? config.label : fieldName;
  };

  // Render facets - only show configured facets
  const renderFacets = () => {
    if (Object.keys(facetData).length === 0 || facetConfig.length === 0) return null;
    
    // Filter facetData to only include configured facets
    const configuredFacets = facetConfig
      .map(config => ({
        field: config.field,
        label: config.label,
        values: facetData[config.field] || []
      }))
      .filter(facet => facet.values.length > 0);
    
    if (configuredFacets.length === 0) return null;
    
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #eee',
        fontSize: '12px'
      }}>
        <div style={{ fontWeight: '600', marginBottom: '8px' }}>Filters:</div>
        {configuredFacets.map(({ field, label, values }) => (
          <div key={field} style={{ marginBottom: '8px' }}>
            <div style={{ fontWeight: '500', marginBottom: '4px' }}>{label}:</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {values
                .filter(({ value }) => value !== null && value !== undefined && value !== "")
                .slice(0, 5)
                .map(({ value, count }) => (
                <span
                  key={value}
                  style={{
                    padding: '2px 6px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '10px',
                    fontSize: '10px'
                  }}
                >
                  {showFacetCounts ? `${value} (${count})` : value}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search info header */}
      {showSearchInfo && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #eee',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <span style={{ fontWeight: '600' }}>Grid View</span>
            {query && <span style={{ marginLeft: '8px', color: '#666' }}>"{query}"</span>}
            {sortBy && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#999' }}>sorted by {sortBy} {sortOrder === 1 ? '↑' : '↓'}</span>}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {totalCount} items found
          </div>
        </div>
      )}

      {/* Facets */}
      {showFacets && renderFacets()}

      {/* Error display */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderBottom: '1px solid #eee'
        }}>
          ❌ Search error: {error}
        </div>
      )}

      {/* Grid content */}
      <div 
        ref={containerRef}
        style={{ 
          flex: 1, 
          overflow: 'hidden'
        }}
      >
        {items.length === 0 && !loading && !error && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>No results found</div>
            <div style={{ fontSize: '14px' }}>
              {query ? `Try adjusting your search for "${query}"` : 'No items to display'}
            </div>
          </div>
        )}

        {items.length > 0 && columnCount > 0 && (
          <Grid
            columnCount={columnCount}
            columnWidth={actualItemWidth + gap}
            height={containerHeight}
            rowCount={rowCount}
            rowHeight={itemHeight + gap}
            width={containerWidth}
          >
            {GridCell}
          </Grid>
        )}

      </div>
    </div>
  );
};

export default SearchableGrid;